"use client"

import * as React from "react"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, NotebookPen, CheckCircle, AlertCircle } from "lucide-react"
import type { Mail } from "@/lib/data"
import { conversationsApi } from "@/services/conversationsApi"

interface ConversationInstructionsProps {
  mail: Mail | null
}

const ConversationInstructionsComponent = ({ mail }: ConversationInstructionsProps) => {
  const [instructions, setInstructions] = React.useState("")
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)

  React.useEffect(() => {
    let isMounted = true

    const loadInstructions = async () => {
      if (!mail?.id) {
        setInstructions("")
        return
      }

      if (mail.platform === "phone_call") {
        setInstructions("")
        return
      }

      setLoading(true)
      setError(null)

      try {
        const data = await conversationsApi.getConversationInstructions(mail.id)
        if (!isMounted) return
        setInstructions(data?.conversation_instructions || "")
      } catch {
        if (!isMounted) return
        setError("No se pudieron cargar las instrucciones de esta conversación.")
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadInstructions()

    return () => {
      isMounted = false
    }
  }, [mail?.id, mail?.platform])

  React.useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => setSuccess(null), 4000)
    return () => clearTimeout(timer)
  }, [success])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!mail?.id || saving || loading || mail.platform === "phone_call") return

    setSaving(true)
    setError(null)

    try {
      await conversationsApi.saveConversationInstructions(mail.id, instructions)
      setInstructions(instructions.trim())
      setSuccess("Instrucciones guardadas correctamente.")
    } catch {
      setError("No se pudieron guardar las instrucciones. Intenta nuevamente.")
    } finally {
      setSaving(false)
    }
  }

  if (!mail) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <NotebookPen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">No conversation selected</h3>
          <p className="text-sm text-muted-foreground">
            Choose a conversation to configure instructions
          </p>
        </div>
      </div>
    )
  }

  if (mail.platform === "phone_call") {
    return (
      <div className="p-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Las instrucciones por conversación están disponibles solo para conversaciones de chat.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto space-y-4 p-4">
      {success && (
        <Alert className="border-success/20 bg-success/10">
          <CheckCircle className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <NotebookPen className="h-5 w-5 text-primary" />
            Instrucciones de conversación
          </CardTitle>
          <CardDescription>
            Define indicaciones específicas para esta conversación que el agente debe priorizar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Ej: Enfocar propuesta en propiedades familiares de 3 habitaciones y evitar inmuebles para reforma."
              className="min-h-[140px]"
              disabled={loading || saving}
            />
            <Button type="submit" className="w-full" disabled={loading || saving}>
              {loading || saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loading ? "Cargando..." : "Guardando..."}
                </>
              ) : (
                "Guardar instrucciones"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export const ConversationInstructions = React.memo(ConversationInstructionsComponent)
